import { _ } from "js/vendor";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import getLogger from "js/core/logger";
import Dropbox from "js/core/utilities/dropbox";
import { trackActivity } from "js/core/utilities/utilities";

import { downloadFileFromOneDrive, downloadFileFromBynder, downloadFileFromBox, downloadMediaFileFromGDrive } from "js/core/utilities/downloadFile";
import { isValidMediaType } from "js/core/utilities/imageUtilities";

const logger = getLogger();

const checkFileTypeAndSize = file => {
    const maxVideSizeBytes = 500 * 1024 * 1024; // 500 MB
    const maxImageSizeBytes = 50 * 1024 * 1024; // 50 MB

    const mediaValidation = isValidMediaType(file);

    if (!mediaValidation.isValidMedia) {
        throw Error("Media format not supported. Only image formats JPEG, PNG, GIF, SVG and most video formats are permitted.");
    }

    if (
        (mediaValidation.isValidImage && file.size > maxImageSizeBytes) ||
        (mediaValidation.isValidVideo && file.size > maxVideSizeBytes)
    ) {
        throw Error("File is too large to upload.");
    }
};

const handleFileSelectChange = (event, uploadFile) => {
    try {
        const files = event.target?.files || event.dataTransfer?.files || event.clipboardData?.files;
        const file = files[0];

        checkFileTypeAndSize(file);
        uploadFile(file);
        trackActivity("Import", "Asset", null, null, {
            source: "Personal Device"
        });
    } catch (err) {
        logger.error(err, "[ImportVideoHelpers] handleFileSelectChange() failed");
        ShowErrorDialog({
            error: "Sorry, we could not process your request",
            message: err.message
        });
    }
};

const handleDropboxClick = async (event, uploadFile, extensions = [".jpg", ".jpeg", ".png", ".svg", ".gif"]) => {
    try {
        const res = await Dropbox.chooseAndGetFile(extensions);
        if (!res.cancelled) {
            checkFileTypeAndSize(res);
            uploadFile(res);
            trackActivity("Import", "Asset", null, null, {
                source: "Dropbox"
            });
        }
    } catch (err) {
        logger.error(err, "[ImportVideoHelpers] handleDropboxClick() failed");
        ShowErrorDialog({
            error: "Sorry, we could not process your request",
            message: err.message
        });
    }
};

const handleBoxSelect = async (info, uploadFile) => {
    try {
        const res = await downloadFileFromBox(info);
        if (!res.cancelled) {
            checkFileTypeAndSize(res);
            uploadFile(res);
            trackActivity("Import", "Asset", null, null, {
                source: "Box"
            });
        }
    } catch (err) {
        logger.error(err, "[ImportVideoHelpers] handleBoxSelect() failed");
        ShowErrorDialog({
            error: "Sorry, we could not process your request",
            message: err.message
        });
    }
};

const handleGoogleFileSelected = async (info, uploadFile) => {
    try {
        const res = await downloadMediaFileFromGDrive(info);
        if (!res.cancelled) {
            checkFileTypeAndSize(res);
            uploadFile(res);
            trackActivity("Import", "Asset", null, null, {
                source: "Google"
            });
        }
    } catch (err) {
        logger.error(err, "[ImportVideoHelpers] handleGoogleFileSelected() failed");
        ShowErrorDialog({
            error: "Sorry, we could not process your request",
            message: err.message
        });
    }
};

const handleOneDriveFileSelected = async (info, uploadFile) => {
    try {
        const res = await downloadFileFromOneDrive(info);
        if (!res.cancelled) {
            checkFileTypeAndSize(res);
            uploadFile(res);
            trackActivity("Import", "Asset", null, null, {
                source: "OneDrive"
            });
        }
    } catch (err) {
        logger.error(err, "[ImportVideoHelpers] handleOneDriveFileSelected() failed");
        ShowErrorDialog({
            error: "Sorry, we could not process your request",
            message: err.message
        });
    }
};

const handleBynderFileSelected = async (info, uploadFile) => {
    try {
        const res = await downloadFileFromBynder(info);
        if (!res.cancelled) {
            checkFileTypeAndSize(res);
            uploadFile(res);
            trackActivity("Import", "Asset", null, null, {
                source: "Bynder"
            });
        }
    } catch (err) {
        logger.error(err, "[ImportVideoHelpers] handleBynderFileSelected() failed");
        ShowErrorDialog({
            error: "Sorry, we could not process your request",
            message: err.message
        });
    }
};

const handleFilePaste = (event, uploadFile) => {
    if (event.clipboardData?.files.length) {
        handleFileSelectChange(event, uploadFile);
    }
};

export { handleFilePaste, handleBynderFileSelected, handleGoogleFileSelected, handleBoxSelect, handleOneDriveFileSelected, handleDropboxClick, handleFileSelectChange, checkFileTypeAndSize };
